import {
  GlobalNav,
  AccountGroup,
  CurrentAccountInfo,
} from '@amzn/aac-chrome-internal-api';

export interface NavStateDiff {
  match: boolean;
  reasons: string[];
}

const MAX_REASONS = 20;

// Fields excluded from the comparison. These are known divergences between
// BTAS navState and TrimService responses, or fields owned by only one side.
const IGNORED_ACCOUNT_FIELDS = new Set(['url']);

const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([k]) => k !== 'url')
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => [k, canonicalize(v)] as const);
    const out: Record<string, unknown> = {};
    for (const [k, v] of entries) out[k] = v;
    return out;
  }
  return value;
};

const findFirstDiff = (
  a: unknown,
  b: unknown,
  path: string,
  reasons: string[],
): void => {
  if (reasons.length >= MAX_REASONS) return;
  if (a === b) return;
  if (a == null || b == null || typeof a !== typeof b) {
    reasons.push(path || '<root>');
    return;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      reasons.push(`${path}.length`);
      return;
    }
    for (let i = 0; i < a.length; i += 1) {
      findFirstDiff(a[i], b[i], `${path}[${i}]`, reasons);
      if (reasons.length >= MAX_REASONS) return;
    }
    return;
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const keys = new Set([
      ...Object.keys(a as object),
      ...Object.keys(b as object),
    ]);
    const sorted = Array.from(keys).sort();
    for (const k of sorted) {
      findFirstDiff(
        (a as Record<string, unknown>)[k],
        (b as Record<string, unknown>)[k],
        path ? `${path}.${k}` : k,
        reasons,
      );
      if (reasons.length >= MAX_REASONS) return;
    }
    return;
  }
  reasons.push(path || '<root>');
};

/**
 * Deep-equal comparison of two globalNav trees, ignoring URL fields (which
 * TrimService reconstructs). Empty/missing globalNav (`""` from BTAS spoofing
 * or `undefined` from a failed load) are treated as equal.
 */
export const diffGlobalNav = (
  btas: GlobalNav | string | undefined | null,
  trim: GlobalNav | string | undefined | null,
): NavStateDiff => {
  // Empty-vs-missing: both sides may return "" (BTAS spoofing / quickNav) or
  // undefined. Treat all of these as equivalent — no signal here.
  const btasEmpty = !btas || typeof btas === 'string';
  const trimEmpty = !trim || typeof trim === 'string';
  if (btasEmpty && trimEmpty) return { match: true, reasons: [] };
  if (btasEmpty !== trimEmpty) {
    return { match: false, reasons: ['globalNav.emptyness'] };
  }
  const canonicalBtas = canonicalize(btas);
  const canonicalTrim = canonicalize(trim);
  const reasons: string[] = [];
  findFirstDiff(canonicalBtas, canonicalTrim, 'globalNav', reasons);
  const truncated =
    reasons.length >= MAX_REASONS ? [...reasons, '...more truncated'] : reasons;
  return { match: reasons.length === 0, reasons: truncated };
};

const normalizeAccountGroups = (
  groups: AccountGroup[] | undefined,
): unknown => {
  if (!groups || groups.length === 0) return [];
  return [...groups]
    .sort((a, b) => a.accountType.localeCompare(b.accountType))
    .map((group) => ({
      accountType: group.accountType,
      label: group.label,
      accounts: [...group.accounts]
        .sort((a, b) => (a.entityId || '').localeCompare(b.entityId || ''))
        .map((account) => {
          const out: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(account)) {
            if (IGNORED_ACCOUNT_FIELDS.has(k)) continue;
            out[k] = v;
          }
          return out;
        }),
    }));
};

/**
 * Deep-equal comparison of the grouped account tree. Compares the `.accounts`
 * subtree only — other CurrentAccountInfo fields (currentEntityId, isAdmin,
 * marketplace, etc.) are NOT part of this diff by design; they're expected to
 * be one-liner scalars that either match by construction or need their own
 * slice. Normalizes group order (by accountType) and account order within a
 * group (by entityId), ignores `url` within accounts. Empty `[]` and missing
 * account arrays are treated as equal.
 */
export const diffAccountInfo = (
  btas: Pick<CurrentAccountInfo, 'accounts'> | undefined | null,
  trim: Pick<CurrentAccountInfo, 'accounts'> | undefined | null,
): NavStateDiff => {
  const btasGroups = btas?.accounts;
  const trimGroups = trim?.accounts;
  const btasEmpty = !btasGroups || btasGroups.length === 0;
  const trimEmpty = !trimGroups || trimGroups.length === 0;
  if (btasEmpty && trimEmpty) return { match: true, reasons: [] };
  if (btasEmpty !== trimEmpty) {
    return { match: false, reasons: ['accountInfo.accounts.emptyness'] };
  }
  const canonicalBtas = normalizeAccountGroups(btasGroups);
  const canonicalTrim = normalizeAccountGroups(trimGroups);
  const reasons: string[] = [];
  findFirstDiff(canonicalBtas, canonicalTrim, 'accountInfo.accounts', reasons);
  const truncated =
    reasons.length >= MAX_REASONS ? [...reasons, '...more truncated'] : reasons;
  return { match: reasons.length === 0, reasons: truncated };
};
